import type { StoryStateType } from "../types";

// Tables 2.0 in-cell elements are direct children of the table
// (`parentId = tableId`, `settings.cellId = <ITableCell.id>`); per-cell order
// is derived from `table.childIds` filtered by `cellId` (see
// `getTableCellChildrenIndex`). Moving one is therefore always a
// remove-then-splice on SOME container's `childIds`, plus (for a cell target)
// a `settings.cellId` patch — never a `cellGroupIndex`/combo shell rewrite.
export interface MoveInCellElementPayload {
  elementId: string;
  // Table (cell target) or slide-side container (to-slide target) to splice
  // the element into.
  toContainerId: string;
  // Present for same-cell/cross-cell moves; absent strips `settings.cellId`
  // (table -> slide).
  toCellId?: string;
  // When `toCellId` is set: desired position among the target cell's OTHER
  // children (i.e. as if the moved element were already removed). Otherwise:
  // absolute index within `toContainerId.childIds`.
  index: number;
}

// Snapshot of the element's prior position, sufficient to invert the move.
export interface MoveInCellElementInverse {
  elementId: string;
  fromContainerId: string;
  fromCellId?: string;
  // Absolute index within `fromContainerId.childIds` before the move.
  fromIndex: number;
}

const cellSiblingsOf = (
  flatContentLayers: StoryStateType["flatContentLayers"],
  childIds: string[],
  cellId: string,
): string[] =>
  childIds.filter((id) => flatContentLayers[id]?.settings.cellId === cellId);

// Resolves the target's cell-scoped `index` (or absolute index for a slide
// target) to an absolute splice index into `toContainer.childIds`. Anchoring
// on a sibling's current position (rather than counting cells directly)
// keeps this correct regardless of how other cells' children are
// interleaved in `table.childIds`.
// Exported so insert-into-cell can resolve a cell-local index the same way
// moves do (anchor on a sibling's position in the interleaved childIds list).
export const resolveAbsoluteIndex = (
  flatContentLayers: StoryStateType["flatContentLayers"],
  toContainerChildIds: string[],
  toCellId: string | undefined,
  index: number,
): number => {
  if (toCellId === undefined) {
    return Math.max(0, Math.min(index, toContainerChildIds.length));
  }
  const siblings = cellSiblingsOf(
    flatContentLayers,
    toContainerChildIds,
    toCellId,
  );
  const clamped = Math.max(0, Math.min(index, siblings.length));
  if (clamped < siblings.length) {
    return toContainerChildIds.indexOf(siblings[clamped]);
  }
  const lastSibling = siblings[siblings.length - 1];
  return lastSibling
    ? toContainerChildIds.indexOf(lastSibling) + 1
    : toContainerChildIds.length;
};

// Applies one in-cell element move to a mutable (immer draft) state.
// Returns the inverse snapshot needed to undo the move, or `undefined` when
// the move couldn't be applied (unknown element/target container) — callers
// should treat `undefined` as a no-op.
export const applyInCellElementMove = (
  state: Pick<StoryStateType, "flatContentLayers">,
  payload: MoveInCellElementPayload,
): MoveInCellElementInverse | undefined => {
  const { elementId, toContainerId, toCellId, index } = payload;
  const { flatContentLayers } = state;
  const element = flatContentLayers[elementId];
  const toContainer = flatContentLayers[toContainerId];
  if (!element || !toContainer) return undefined;

  const fromContainerId = element.parentId;
  const fromContainer = fromContainerId
    ? flatContentLayers[fromContainerId]
    : undefined;
  if (!fromContainer) return undefined;
  const fromIndex = fromContainer.childIds.indexOf(elementId);
  if (fromIndex < 0) return undefined;
  const fromCellId = element.settings.cellId as string | undefined;

  // Remove first — for a same-container move this is the SAME array the
  // target index is resolved against, so the anchor lookup below already
  // sees the post-removal positions.
  fromContainer.childIds.splice(fromIndex, 1);

  const absoluteIndex = resolveAbsoluteIndex(
    flatContentLayers,
    toContainer.childIds,
    toCellId,
    index,
  );
  toContainer.childIds.splice(absoluteIndex, 0, elementId);

  element.parentId = toContainerId;
  if (toCellId !== undefined) {
    element.settings.cellId = toCellId;
  } else {
    delete element.settings.cellId;
  }

  return { elementId, fromContainerId: fromContainerId as string, fromCellId, fromIndex };
};

// Inverts a prior `applyInCellElementMove` call, restoring the element to its
// exact prior container/cell/index.
export const undoInCellElementMove = (
  state: Pick<StoryStateType, "flatContentLayers">,
  inverse: MoveInCellElementInverse,
): void => {
  const { elementId, fromContainerId, fromCellId, fromIndex } = inverse;
  const { flatContentLayers } = state;
  const element = flatContentLayers[elementId];
  const fromContainer = flatContentLayers[fromContainerId];
  if (!element || !fromContainer) return;

  const currentContainer = element.parentId
    ? flatContentLayers[element.parentId]
    : undefined;
  if (currentContainer) {
    const i = currentContainer.childIds.indexOf(elementId);
    if (i >= 0) currentContainer.childIds.splice(i, 1);
  }

  const clamped = Math.max(
    0,
    Math.min(fromIndex, fromContainer.childIds.length),
  );
  fromContainer.childIds.splice(clamped, 0, elementId);
  element.parentId = fromContainerId;
  if (fromCellId !== undefined) {
    element.settings.cellId = fromCellId;
  } else {
    delete element.settings.cellId;
  }
};
